import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import PERM from 'constants/core/perm.const';
import { useAccessIds, useCustomPermission } from 'providers/Access.provider';
import { useTranslation } from 'providers/GILT.provider';
import { selectAccessHasLockToken } from 'selectors/access.selectors';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';

import { displayPermissionToast } from 'notification/actions';

/**
 * Online booking permission flags + guards (GS online booking phase 3).
 *
 * Mirrors `useAppointmentPermissions`: the backend only includes a permission
 * ID in the lock token when the value is granted (`all_staff` / `personal` for
 * radio permissions, `true` for boolean permissions). A `no_access` / `false`
 * value is represented by the ID being absent, so a missing ID means "denied"
 * for any user that has a lock token. When there is no lock token (master /
 * manager session without a PIN) every check defaults to allowed.
 *
 * The `block*` helpers follow the return-true-to-short-circuit pattern: they
 * surface the relevant permission toast and return `true` when the action is
 * restricted, so callers can `if (blockX(...)) return;`.
 *
 * - `can_edit_availability` (true/false)
 * - `can_amend_online_services` (all_staff/personal/no_access)
 * - `can_manage_online_deposit_settings` (all_staff/no_access)
 */
export const useOnlineBookingPermissions = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const hasLockToken = useSelector(selectAccessHasLockToken);
  const { accessStylistId } = useAccessIds();

  const canAmendServicesAll = useCustomPermission(PERM.canAmendOnlineServicesAllStaff);
  const canAmendServicesPersonal = useCustomPermission(PERM.canAmendOnlineServicesPersonal);
  const canEditAvailabilityRaw = useCustomPermission(PERM.canEditAvailability);
  const canManageDepositAll = useCustomPermission(PERM.canManageOnlineDepositSettingsAllStaff);

  const canManageDepositSettings = !hasLockToken || canManageDepositAll;
  const canEditAvailability = !hasLockToken || canEditAvailabilityRaw;
  const isAmendServicesBlocked = hasLockToken && !canAmendServicesAll && !canAmendServicesPersonal;
  const isAmendServicesPersonalOnly = hasLockToken && canAmendServicesPersonal && !canAmendServicesAll;

  const blockAmendOnlineServices = useCallback(
    (stylistId) => {
      if (isAmendServicesBlocked) {
        dispatch(displayPermissionToast(t('onlineBooking.permissions.amendServicesNoAccess'), 'ob-services-no-access'));
        return true;
      }
      if (isAmendServicesPersonalOnly && fromAnyToTrimmed(stylistId) !== fromAnyToTrimmed(accessStylistId)) {
        dispatch(displayPermissionToast(t('onlineBooking.permissions.amendServicesPersonalOnly'), 'ob-services-personal'));
        return true;
      }
      return false;
    },
    [accessStylistId, dispatch, isAmendServicesBlocked, isAmendServicesPersonalOnly, t],
  );

  const blockEditAvailability = useCallback(() => {
    if (canEditAvailability) return false;
    dispatch(displayPermissionToast(t('onlineBooking.permissions.editAvailabilityNoAccess'), 'ob-availability'));
    return true;
  }, [canEditAvailability, dispatch, t]);

  return useMemo(
    () => ({
      canManageDepositSettings,
      blockAmendOnlineServices,
      blockEditAvailability,
    }),
    [blockAmendOnlineServices, blockEditAvailability, canManageDepositSettings],
  );
};
